/**
 * Formatting. Indian conventions, because the tenant and the customers are Indian:
 * lakh/crore grouping for rupees, Asia/Kolkata for wall-clock times.
 *
 * Author: Danish Khan
 */

const inr = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});
const inrPaise = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
});

export function formatInr(value: number, paise = false): string {
  return (paise ? inrPaise : inr).format(value);
}

export function formatPriceRange(min: number, max: number): string {
  return min === max ? formatInr(min) : `${formatInr(min)} – ${formatInr(max)}`;
}

/** "4m", "2h 10m", "3d" — compact enough for an SLA clock in a table cell. */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 === 0 ? `${h}h` : `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return h % 24 === 0 ? `${d}d` : `${d}d ${h % 24}h`;
}

export function formatAgo(iso: string, now = Date.now()): string {
  const ms = now - Date.parse(iso);
  if (!Number.isFinite(ms)) return '—';
  /**
   * The future is not "just now". `GET /leads/:id` returns every OPEN promise, not only the
   * overdue ones, so `dueAt` is routinely ahead of the clock — and a negative `ms` fell through
   * the 45-second window below, printing "due just now" on a promise due next Tuesday. The one
   * panel the product claims it does better than the system it replaces was reporting wrong times.
   */
  if (ms <= -45_000) return `in ${formatDuration(-ms)}`;
  if (ms < 45_000) return 'just now';
  return `${formatDuration(ms)} ago`;
}

const timeFmt = new Intl.DateTimeFormat('en-IN', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Asia/Kolkata',
});
const dateFmt = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  timeZone: 'Asia/Kolkata',
});
const fullFmt = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Asia/Kolkata',
});

export const formatTime = (iso: string): string => timeFmt.format(new Date(iso));
export const formatDate = (iso: string): string => dateFmt.format(new Date(iso));
export const formatDateTime = (iso: string): string => `${fullFmt.format(new Date(iso))} IST`;

const round = (n: number, places: number): number => Number(n.toFixed(places));

/** Milliseconds as a person says them. Mirrors `packages/core/src/report/format.ts`. */
export function formatDurationMs(ms: number): string {
  const n = Math.round(ms);
  if (Math.abs(n) < 1000) return `${n} ms`;
  const s = n / 1000;
  if (Math.abs(s) < 90) return `${round(s, 1)}s`;
  const m = s / 60;
  if (Math.abs(m) < 90) return `${round(m, 1)} min`;
  return `${round(m / 60, 1)} h`;
}

/**
 * A metric value, or an em dash. **The only implementation in the console.**
 *
 * `null` means the window held nothing to measure — no conversation opened, no reply timed, no
 * handoff claimed. Rendering that as `0` would read as "instant replies" or "nothing is wrong",
 * which is the exact inversion of what it means. The dash is the whole point of the null.
 *
 * This file used to hold a second, looser version of the same function while `analyticsApi.ts`
 * held this one, so `first_reply_p50` read "4m" on Health and "4.2 min" on Home and Analytics, and
 * `guard_fire_rate` read "12%" against "12.0%" — the same labelled number, written two ways, one
 * tab apart. The server's rendering is the one that wins: it is byte-for-byte what
 * `packages/core/src/report/format.ts` puts in the weekly report, including the four decimals
 * below a rupee (a per-conversation figure of ₹0.0123 shown as "₹0.01" is a nineteen percent error
 * on a number somebody multiplies by forty thousand). `test/Analytics.test.tsx` asserts the pairs.
 */
export function formatMetric(value: number | null, unit: 'count' | 'ms' | 'percent' | 'inr'): string {
  if (value === null || !Number.isFinite(value)) return '—';
  switch (unit) {
    case 'count':
      return Math.round(value).toLocaleString('en-IN');
    case 'percent':
      return `${round(value, 1).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
    case 'ms':
      return formatDurationMs(value);
    case 'inr':
      return `₹${Math.abs(value) < 1 && value !== 0
        ? round(value, 4).toLocaleString('en-IN', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
        : round(value, 2).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

/** The tense, said in words a business owner reads rather than in a field name. */
export const temporalLabel = (t: 'point_in_time' | 'current_state'): string =>
  t === 'point_in_time' ? 'measured from when each event happened' : 'the state right now';

export function initials(name: string): string {
  const parts = name.replace(/[.]/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * A column name as a sentence fragment: `asked_for_call_or_visit` → "Asked for call or visit".
 *
 * The same transform the API applies to score-factor and handoff-brief labels
 * (`routes/leads.ts`, `routes/handoffs.ts`). It is not applied to lead fact keys or promise kinds
 * on the wire, so the console does it rather than printing a database identifier at a customer.
 */
export function humanize(key: string): string {
  const words = key.replace(/_/gu, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The funnel stages, in the words a person uses.
 *
 * `GET /pipeline` is the only route that carries `STAGE_LABELS` (`packages/core/src/api/view.ts`);
 * every other route ships the bare key. The board therefore said "Handed over" while the Inbox
 * said `handed_over` for the same lead. Mirrored here rather than derived, because the vocabulary
 * is 017's CHECK constraint and cannot change without a migration; `stageLabel` falls back to
 * `humanize` so a key this map has not heard of still reads as English.
 */
export const STAGE_LABEL: Record<string, string> = {
  new: 'New',
  engaged: 'Engaged',
  qualified: 'Qualified',
  ready: 'Ready',
  handed_over: 'Handed over',
  closed: 'Closed',
};

export const stageLabel = (key: string): string => STAGE_LABEL[key] ?? humanize(key);

/**
 * `crm.contacts.customer_type`, which the API defaults to `"unknown"` when nobody has set it —
 * so the pill read a literal "unknown" on most contacts. Saying it is not known is the same fact
 * without the database's word for it.
 */
export const customerTypeLabel = (type: string): string =>
  type === 'b2b' ? 'B2B' : type === 'b2c' ? 'B2C' : 'Type not known';

/**
 * A colleague, named rather than addressed.
 *
 * The API identifies whoever claimed a handoff or wrote a note by their email — it is the only
 * identity a session carries — so a pill read `karthik@maincharacterglobal.com has it`. There is
 * no route that maps an address to a display name, so the local part is shown and the full
 * address stays in the title rather than being invented away.
 */
export function personLabel(who: string): string {
  const at = who.indexOf('@');
  return at > 0 ? who.slice(0, at) : who;
}

export const LANG_LABEL: Record<string, string> = {
  en: 'English',
  hi: 'हिन्दी',
  hinglish: 'Hinglish',
  // `convo.conversations.language` is null until something has been detected, which is most of
  // the first turn of most threads. Labelled, not blanked: a blank chip reads as a bug.
  unknown: 'Not yet known',
};
